// ─────────────────────────────────────────────────────────────────────────────
// FUENTE DE VERDAD: ec-bo/src/analytics/sanitize.ts
// Mantener BYTE-IDÉNTICO con ec-portal/src/analytics/sanitize.ts (EC-3811).
//
// PURO a propósito: sin process.env, sin console, sin imports de la app. Quién
// avisa y cómo se comporta en dev lo decide el core vía onRedaction.
//
// Este es el único punto por el que sale información del navegador hacia el
// proveedor de analytics. Es un sistema de salud: la política por defecto es
// descartar, no confiar.
// ─────────────────────────────────────────────────────────────────────────────
import type { SafeProps, Scalar } from "./types";

export type RedactionRule =
  | "not_allowlisted"
  | "denied_key"
  | "email_value"
  | "phone_value"
  | "document_value"
  | "coordinate_value"
  | "token_value"
  | "uuid_value"
  | "too_long"
  | "non_scalar"
  | "array_too_big"
  | "event_dropped";

export interface Redaction {
  key: string;
  rule: RedactionRule;
  /** Lo completa el core; sanitize no conoce nombres de evento. */
  event?: string;
}

export interface SanitizeResult {
  props: SafeProps;
  redactions: Redaction[];
  dropEvent: boolean;
}

// ── Denylist de CLAVES ───────────────────────────────────────────────────────
// Segunda barrera bajo el allowlist derivado del tipo, y barrera ÚNICA para las
// props que genera el SDK (donde no controlamos los nombres de clave).
const DENY_KEY: RegExp[] = [
  /e-?mail/i,
  /first_?name|last_?name|full_?name|user_?name/i,
  /document|dni|nif|cuil|cuit|passport|tax_?id/i,
  /phone|telefono|tel[eé]fono|whatsapp|msisdn|landline/i,
  /(^|_)(lat|lng|lon|long)(_|$)|latitude|longitude|coord|geo/i,
  /address|street|apartment|postal|zip|(^|_)city(_|$)|province/i,
  /birth|dob|(^|_)edad(_|$)|(^|_)age(_|$)/i,
  /observa|comment|note|descrip|free_?text|message|body/i,
  /health_?system|affiliate|insurance|obra_?social/i,
  // `weight`/`height` son métricas corporales (PHI). Como efecto colateral
  // aceptado, esto también descarta $screen_height/$viewport_height del SDK:
  // no las necesitamos — para "desde qué dispositivo" alcanzan $device_type,
  // $browser y $os, que sí pasan.
  /trigger_?value|heart_?rate|(^|_)bpm(_|$)|spo2|pressure|glucose|sleep|weight|height/i,
  /token|jwt|password|secret|api_?key|authorization|credential/i,
  /code_?word|palabra_?clave|keyword/i,
  /patient|imei|serial|smartwatch_?id|support_?network_?id|device_?token|endpoint_?arn/i,
  /url|referrer|href|pathname|(^|_)host(_|$)/i,
  /photo|avatar|image/i,
];

// ── Heurísticas de VALOR ─────────────────────────────────────────────────────
// Para claves permitidas cuyo CONTENIDO resulta ser PII. Es el caso más común:
// la clave es legítima (`reason`, `filter`) y alguien le puso un email adentro.
const EMAIL = /[^\s@]+@[^\s@]+\.[a-z]{2,}/i;
const PHONE = /(?:\+?\d[\s().-]?){8,}/;
const JWT = /\beyJ[A-Za-z0-9_-]{10,}\./;
const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i;
/** El valor ES exactamente un UUID (no que lo contenga). */
const UUID_EXACT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DOCUMENT = /^\d{7,11}$/;

/** Claves donde un UUID es legítimo (tenant, no persona). Cerrado a mano. */
const UUID_ALLOWED_KEYS = new Set<string>(["company_client_id", "home_company_client_id"]);

/**
 * Sobre de transporte de PostHog. NO son propiedades de evento: son la plomería
 * que el endpoint de ingesta exige para aceptar el POST.
 *
 * Sin `token` y `distinct_id` el server responde 400 y posthog-js reintenta con
 * backoff para siempre ("Enqueued failed request for retry in ..."), o sea
 * analytics queda roto de punta a punta sin un solo error visible.
 *
 * Caían de rebote por dos reglas que están bien: `uuid_value` rechaza todo UUID
 * —para que no se filtre el de un paciente— y la denylist rechaza las claves que
 * se llaman `token` —por el JWT del SSO—. Pero acá los cinco valores son del
 * propio SDK y ninguno puede cargar PII:
 *
 *   token       → nuestra project API key. Es pública por diseño: viaja en el
 *                 bundle del browser. Se exige la forma `phc_…` justamente para
 *                 que esta excepción NO pueda dejar pasar un JWT del SSO.
 *   distinct_id → el id del operador, que ya mandamos a propósito en identify.
 *   $session_id / $window_id / $device_id → aleatorios que genera el SDK, sin
 *                 relación con ningún dato de persona.
 *
 * Cerrado a mano y a propósito: no se relaja a "cualquier clave que termine en
 * _id", que es exactamente el agujero por el que se irían los UUID de paciente.
 * Sólo aplica a sanitizeAutoProps (props del SDK). El sanitizador de las props
 * declaradas por nosotros sigue igual de estricto.
 */
const POSTHOG_PROJECT_KEY = /^phc_[A-Za-z0-9]{20,}$/;

const POSTHOG_SDK_ID_KEYS = new Set<string>([
  "distinct_id",
  "$session_id",
  "$window_id",
  "$device_id",
]);

function isTransportField(key: string, value: unknown): boolean {
  if (typeof value !== "string") return false;
  if (key === "token") return POSTHOG_PROJECT_KEY.test(value);
  return POSTHOG_SDK_ID_KEYS.has(key);
}

/** Claves donde un decimal es legítimo. Vacío: cualquier float huele a coordenada. */
const FLOAT_ALLOWED_KEYS = new Set<string>([]);

const MAX_STRING_LENGTH = 100;
const MAX_ARRAY_LENGTH = 10;

/**
 * Umbral de descarte del evento entero. Tres props malas no son un descuido:
 * es alguien que pasó una entidad completa, p.ej. track('x', patient).
 * Una sola prop mala no debe costar el funnel entero.
 */
const DROP_EVENT_THRESHOLD = 3;

const isDeniedKey = (key: string): boolean => DENY_KEY.some((re) => re.test(key));

function checkString(key: string, value: string): RedactionRule | null {
  if (value.length > MAX_STRING_LENGTH) return "too_long";

  // Un UUID exacto en una clave de tenant es legítimo y sale temprano: si
  // siguiera, PHONE daría un falso positivo sobre los propios dígitos del UUID
  // ("…5717-4562…" son 8 grupos de dígito+separador). Un valor que sólo
  // CONTIENE un UUID no entra por acá y se sigue rechazando más abajo.
  if (UUID_ALLOWED_KEYS.has(key) && UUID_EXACT.test(value)) return null;

  if (EMAIL.test(value)) return "email_value";
  if (JWT.test(value)) return "token_value";
  if (UUID.test(value)) return "uuid_value";
  if (DOCUMENT.test(value)) return "document_value";
  if (PHONE.test(value)) return "phone_value";
  return null;
}

function checkNumber(key: string, value: number): RedactionRule | null {
  if (!Number.isFinite(value)) return "non_scalar";
  // Sólo enteros salvo excepción explícita: descarta lat/long y promedios de bpm.
  if (!Number.isInteger(value) && !FLOAT_ALLOWED_KEYS.has(key)) return "coordinate_value";
  return null;
}

/**
 * Sanea las props de NUESTROS eventos.
 *
 * Orden: allowlist (derivado de AnalyticsEventMap) → denylist de claves →
 * heurísticas de valor.
 *
 * Política:
 *  - Prop rechazada: SE DESCARTA. No se reemplaza por "[REDACTED]" — un marcador
 *    ensucia los breakdowns y le hace creer al analista que hay un valor.
 *  - Se deja rastro SIN VALORES en `_redacted_keys` y `_redaction_count`, para
 *    poder alertar en producción sobre `_redaction_count > 0`.
 *  - String > 100 chars: se tira el contenido, se conserva `<key>_length`.
 *  - Objetos y arrays anidados: se descartan siempre. Es la vía #1 por la que
 *    entra una entidad completa.
 */
export function sanitizeProps(
  raw: Record<string, unknown>,
  allow: ReadonlySet<string>
): SanitizeResult {
  const props: SafeProps = {};
  const redactions: Redaction[] = [];
  const drop = (key: string, rule: RedactionRule): void => {
    redactions.push({ key, rule });
  };

  for (const [key, value] of Object.entries(raw)) {
    if (value === undefined) continue;

    if (!allow.has(key)) {
      drop(key, "not_allowlisted");
      continue;
    }
    if (isDeniedKey(key)) {
      drop(key, "denied_key");
      continue;
    }

    if (value === null || typeof value === "boolean") {
      props[key] = value;
      continue;
    }

    if (typeof value === "number") {
      const bad = checkNumber(key, value);
      if (bad) drop(key, bad);
      else props[key] = value;
      continue;
    }

    if (typeof value === "string") {
      const bad = checkString(key, value);
      if (bad === "too_long") {
        drop(key, bad);
        props[`${key}_length`] = value.length;
        continue;
      }
      if (bad) {
        drop(key, bad);
        continue;
      }
      props[key] = value;
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length > MAX_ARRAY_LENGTH) {
        drop(key, "array_too_big");
        continue;
      }
      const clean: Scalar[] = [];
      let ok = true;
      for (const item of value) {
        if (typeof item === "string") {
          if (checkString(key, item)) {
            ok = false;
            break;
          }
          clean.push(item);
        } else if (typeof item === "number") {
          if (checkNumber(key, item)) {
            ok = false;
            break;
          }
          clean.push(item);
        } else if (typeof item === "boolean" || item === null) {
          clean.push(item);
        } else {
          ok = false;
          break;
        }
      }
      if (ok) props[key] = clean;
      else drop(key, "non_scalar");
      continue;
    }

    // Objetos, Date, Map, funciones, symbols…
    drop(key, "non_scalar");
  }

  if (redactions.length > 0) {
    props._redacted_keys = redactions.map((r) => r.key).slice(0, 10);
    props._redaction_count = redactions.length;
  }

  return { props, redactions, dropEvent: redactions.length >= DROP_EVENT_THRESHOLD };
}

/**
 * Sanea las props que genera el SDK (auto-capturadas). No hay allowlist posible
 * acá porque las claves son del proveedor ($current_url, $referrer, $set…), así
 * que sólo corre denylist + heurísticas. Nunca dropea el evento: el hook del SDK
 * no puede hacerlo.
 *
 * Recorre $set y $set_once, que es donde PostHog guarda los $initial_* — el
 * lugar exacto donde quedaría persistida la URL del /sso-bridge con el JWT.
 */
export function sanitizeAutoProps(props: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(props)) {
    if (key === "$set" || key === "$set_once") {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        out[key] = sanitizeAutoProps(value as Record<string, unknown>);
      }
      continue;
    }

    // El sobre de transporte pasa entero: ver POSTHOG_SDK_ID_KEYS.
    if (isTransportField(key, value)) {
      out[key] = value;
      continue;
    }

    if (isDeniedKey(key)) continue;

    if (typeof value === "string") {
      if (checkString(key, value)) continue;
      out[key] = value;
      continue;
    }

    if (typeof value === "number" || typeof value === "boolean" || value === null) {
      out[key] = value;
      continue;
    }

    if (Array.isArray(value)) {
      out[key] = value.filter((v) => typeof v !== "object" || v === null);
      continue;
    }

    // Objetos anidados desconocidos: fuera.
  }

  return out;
}
